import { FC, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Box, Button, Callout, Card, Flex, Separator, Table, Text } from "@radix-ui/themes";
import { SimpleAccordion } from "@/components/ui/accordion";
import { DateTime } from "luxon";

import { atlasRoutes } from "@/app/router/routes";
import { Uuid } from "@/domains/global/identifiers";
import {
  SmartEngineRun,
  SmartEngineRunEvent,
  SmartEngineRunPayload,
} from "../shared/engine-run-api";
import { useSmartEngineRun } from "../shared/use-smart-engine-runs";

const STAGE_LABELS: Record<string, string> = {
  sample_items: "Sample Items",
  mix_items: "Mix Items",
  hydrate_items: "Hydrate Items",
  build_agent_context: "Build Agent Context",
  run_agent: "Run Agent",
  mix_actions: "Mix Actions",
  apply_actions: "Apply Actions",
  apply_agent_actions: "Apply Agent Actions",
};

const formatTimestamp = (iso?: string | null) => {
  if (!iso) {
    return "–";
  }
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  if (!dt.isValid) {
    return iso;
  }
  const relative = dt.toRelative({ base: DateTime.utc(), style: "short" });
  const absolute = dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  return relative ? `${relative} (${absolute})` : absolute;
};

const formatTimestampShort = (iso?: string | null) => {
  if (!iso) {
    return "–";
  }
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  if (!dt.isValid) {
    return iso;
  }
  return dt.toLocaleString(DateTime.TIME_WITH_SECONDS);
};

const formatDuration = (seconds?: number | null) => {
  if (seconds == null) {
    return "–";
  }

  if (seconds >= 0.1) {
    const precision = seconds >= 10 ? 1 : 2;
    return `${seconds.toFixed(precision)}s`;
  }

  const millis = seconds * 1000;
  const precision = millis >= 100 ? 0 : 1;
  return `${millis.toFixed(precision)}ms`;
};

interface StageSegment {
  stage: string;
  startedAt?: string;
  endedAt?: string;
  durationSeconds: number;
  error?: { type?: string | null; message?: string | null };
}

const buildStageSegments = (events: SmartEngineRunEvent[]): StageSegment[] => {
  const pending: SmartEngineRunEvent[] = [];
  const segments: StageSegment[] = [];

  events.forEach(event => {
    const stage = event.stage;
    if (!stage) {
      return;
    }

    if (event.event === "STAGE_BEGIN") {
      pending.push(event);
      return;
    }

    if (event.event === "STAGE_END") {
      const matchIndex = [...pending].reverse().findIndex(e => e.stage === stage);
      if (matchIndex >= 0) {
        const actualIndex = pending.length - 1 - matchIndex;
        const begin = pending.splice(actualIndex, 1)[0];
        const duration =
          event.duration_seconds ??
          (begin.timestamp && event.timestamp
            ? (DateTime.fromISO(event.timestamp).diff(DateTime.fromISO(begin.timestamp), "seconds")
                .seconds ?? 0)
            : 0);
        segments.push({
          stage,
          startedAt: begin.timestamp,
          endedAt: event.timestamp,
          durationSeconds: Math.max(duration ?? 0, 0),
        });
      }
      return;
    }

    if (event.event === "ERROR") {
      const lastSegment = segments.at(-1);
      if (lastSegment && lastSegment.stage === stage) {
        lastSegment.error = {
          type: event.error_type,
          message: event.error_message,
        };
      }
    }
  });

  return segments.sort((a, b) => {
    const aTime = a.startedAt ? DateTime.fromISO(a.startedAt).toMillis() : 0;
    const bTime = b.startedAt ? DateTime.fromISO(b.startedAt).toMillis() : 0;
    return aTime - bTime;
  });
};

const openLogfireSearch = (query: string) => {
  const encoded = encodeURIComponent(query);
  const url = `https://logfire-us.pydantic.dev/mem/backend?q=${encoded}&last=1d`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const buildMessageLikeQuery = (value: string) => `message ILIKE '%${value}%'`;

export const SmartEngineRunPage: FC = () => {
  const { smartEngineRunId } = useParams<{ smartEngineRunId: Uuid }>();

  if (!smartEngineRunId) {
    return <div>Invalid Smart Engine run ID</div>;
  }

  const query = useSmartEngineRun(smartEngineRunId);
  const run = query.data;
  const payload = run?.payload;

  const handleOpenLogfireByRunId = () => {
    openLogfireSearch(buildMessageLikeQuery(smartEngineRunId));
  };

  return (
    <Box>
      <Flex align="center" justify="between" mb="4">
        <Box>
          <Text as="div" size="4" weight="bold">
            Smart Engine Run
          </Text>
          <Text as="div" size="2" color="gray">
            ID: {smartEngineRunId}
          </Text>
        </Box>
        <Flex gap="2">
          {run?.associated_smart_view_id && (
            <Button asChild variant="outline">
              <Link
                to={
                  atlasRoutes.smartViewsView({
                    params: { smartViewId: run.associated_smart_view_id },
                  }).path
                }
              >
                Open Smart View
              </Link>
            </Button>
          )}
          <Button variant="outline" onClick={handleOpenLogfireByRunId}>
            View in Logfire
          </Button>
          <Button asChild variant="soft">
            <Link to={atlasRoutes.smartViews({}).path}>Back to Smart Views</Link>
          </Button>
        </Flex>
      </Flex>

      {query.isLoading && <Text size="2">Loading run…</Text>}
      {query.isError && (
        <Text size="2" color="red">
          Failed to load run.
        </Text>
      )}

      {run && payload && (
        <Flex direction="column" gap="4">
          <RunSummaryCard run={run} payload={payload} />

          {payload.error && (
            <Callout.Root color="red" variant="surface">
              <Callout.Text>
                <Text size="2" weight="bold">
                  {payload.error.type}
                </Text>
                <Text size="2">{payload.error.message}</Text>
              </Callout.Text>
            </Callout.Root>
          )}

          <RunContextCard run={run} payload={payload} />

          <RunTimelineCard events={payload.events ?? []} timing={payload.timing} />

          <PlannerActionsCard summary={payload.result_summary ?? null} metrics={payload.metrics} />

          <EventLogSection events={payload.events ?? []} timing={payload.timing} />

          <SimpleAccordion title={"Raw Payload"} defaultOpen={false}>
            <Card>
              <CodeBlock value={JSON.stringify(payload, null, 2)} />
            </Card>
          </SimpleAccordion>
        </Flex>
      )}
    </Box>
  );
};

const RunSummaryCard: FC<{ run: SmartEngineRun; payload: SmartEngineRunPayload }> = ({
  run,
  payload,
}) => {
  const duration = payload.timing?.duration_seconds ?? null;
  const metrics = payload.metrics;

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Flex gap="3" wrap="wrap">
          <SummaryStat label="Status">
            <Badge
              size="1"
              color={
                run.status === "SUCCEEDED" ? "green" : run.status === "FAILED" ? "red" : "gray"
              }
            >
              {run.status}
            </Badge>
          </SummaryStat>
          <SummaryStat label="Mode" value={run.mode} />
          <SummaryStat label="Started" value={formatTimestamp(run.started_at)} />
          <SummaryStat label="Ended" value={formatTimestamp(run.ended_at)} />
          <SummaryStat label="Duration" value={formatDuration(duration)} />
        </Flex>

        <Separator size="4" />

        <Flex gap="3" wrap="wrap">
          <SummaryStat label="Cards Planned" value={metrics.cards_planned} mutedLabel />
          <SummaryStat label="Cards Filled" value={metrics.cards_filled} mutedLabel />
          <SummaryStat label="Cards Created" value={metrics.cards_created} mutedLabel />
          <SummaryStat label="Smart Entities" value={metrics.entities_created ?? 0} mutedLabel />
          <SummaryStat label="Max Cards Requested" value={metrics.max_cards_requested} mutedLabel />
          <SummaryStat label="Pipeline Started" value={metrics.pipeline_started} mutedLabel />
          <SummaryStat label="Pipeline Completed" value={metrics.pipeline_completed} mutedLabel />
          <SummaryStat label="Pipeline Failed" value={metrics.pipeline_failed} mutedLabel />
          {metrics.error_type && (
            <SummaryStat label="Error Type" value={metrics.error_type} mutedLabel />
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

const RunContextCard: FC<{ run: SmartEngineRun; payload: SmartEngineRunPayload }> = ({
  run,
  payload,
}) => {
  const args = payload.args ?? {};
  const traces = payload.traces ?? {};

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Text size="3" weight="bold">
          Context
        </Text>
        <Flex direction="column" gap="2">
          <InfoBadge label="Space Account" value={run.associated_space_account_id} />
          <InfoBadge label="Smart View" value={run.associated_smart_view_id} />
          <InfoBadge label="Smart Context" value={run.smart_context_id} />
          {args.target_card_id && (
            <InfoBadge label="Target Card" value={String(args.target_card_id)} />
          )}
          {typeof args.max_cards === "number" && (
            <InfoBadge label="Max Cards" value={String(args.max_cards)} />
          )}
        </Flex>

        {(traces.datadog_trace_id ?? traces.logfire_trace_id) && (
          <Box>
            <Text size="2" weight="bold" mb="2">
              Traces
            </Text>
            <Flex direction="column" gap="2">
              {traces.datadog_trace_id && (
                <InfoBadge label="Datadog Trace" value={traces.datadog_trace_id} />
              )}
              {traces.logfire_trace_id && (
                <InfoBadge
                  label="Logfire Trace"
                  value={traces.logfire_trace_id}
                  logfireQuery={buildMessageLikeQuery(traces.logfire_trace_id)}
                />
              )}
            </Flex>
          </Box>
        )}

        <Box>
          <Text size="2" weight="bold" mb="2">
            Existing Smart View Snapshot
          </Text>
          {args.existing_view ? (
            <Flex direction="column" gap="2">
              <InfoBadge label="Card Count" value={String(args.existing_view.card_count)} />
              {args.existing_view.card_ids?.length ? (
                <CodeBlock value={args.existing_view.card_ids.join("\n")} />
              ) : (
                <Text size="1" color="gray">
                  No card IDs recorded.
                </Text>
              )}
            </Flex>
          ) : (
            <Text size="1" color="gray">
              None
            </Text>
          )}
        </Box>

        {args.required_keep_cards && args.required_keep_cards.length > 0 && (
          <Box>
            <Text size="2" weight="bold" mb="2">
              Required Keep Cards
            </Text>
            <Table.Root size="1" variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Card ID</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Required Action</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Anchor Note</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {args.required_keep_cards.map(item => (
                  <Table.Row key={`${item.card_id}-${item.required_action}`}>
                    <Table.Cell>{item.card_id}</Table.Cell>
                    <Table.Cell>{item.required_action}</Table.Cell>
                    <Table.Cell>{item.anchor_note_id ?? "–"}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        <Box>
          <Text size="2" weight="bold" mb="1">
            Raw Context Payload
          </Text>
          <CodeBlock value={JSON.stringify(args.context_payload ?? {}, null, 2)} />
        </Box>
      </Flex>
    </Card>
  );
};

const RunTimelineCard: FC<{
  events: SmartEngineRunEvent[];
  timing?: SmartEngineRunPayload["timing"];
}> = ({ events, timing }) => {
  const segments = useMemo(() => buildStageSegments(events), [events]);
  const totalDuration =
    timing?.duration_seconds ?? segments.reduce((acc, seg) => acc + seg.durationSeconds, 0);

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Text size="3" weight="bold">
          Execution Timeline
        </Text>
        {segments.length === 0 ? (
          <Text size="2" color="gray">
            No stage information recorded.
          </Text>
        ) : (
          <Flex direction="column" gap="3">
            {segments.map(segment => {
              const widthPercentage =
                totalDuration > 0
                  ? Math.max((segment.durationSeconds / totalDuration) * 100, 4)
                  : 100;
              const label = STAGE_LABELS[segment.stage] ?? segment.stage;
              return (
                <Box key={`${segment.stage}-${segment.startedAt}`}>
                  <Flex justify="between" align="center" mb="1">
                    <Text size="2" weight="bold">
                      {label}
                    </Text>
                    <Text size="2" color="gray">
                      {formatDuration(segment.durationSeconds)}
                    </Text>
                  </Flex>
                  <Box
                    style={{
                      background: "var(--gray-4)",
                      borderRadius: 9999,
                      height: 8,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      style={{
                        width: `${widthPercentage}%`,
                        background: segment.error ? "var(--red-9)" : "var(--blue-9)",
                        height: "100%",
                      }}
                    />
                  </Box>
                  <Flex justify="between" mt="1">
                    <Text size="1" color="gray">
                      {formatTimestampShort(segment.startedAt)}
                    </Text>
                    <Text size="1" color="gray">
                      {formatTimestampShort(segment.endedAt)}
                    </Text>
                  </Flex>
                  {segment.error && (
                    <Text size="1" color="red">
                      {segment.error.type}: {segment.error.message}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Card>
  );
};

const PlannerActionsCard: FC<{
  summary: SmartEngineRunPayload["result_summary"] | null;
  metrics: SmartEngineRunPayload["metrics"];
}> = ({ summary, metrics }) => {
  if (!summary) {
    return (
      <Card>
        <Text size="3" weight="bold">
          Planner & Persistence
        </Text>
        <Text size="2" color="gray">
          No planner results recorded.
        </Text>
      </Card>
    );
  }

  const details = summary.planner_action_details ?? [];

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Text size="3" weight="bold">
          Planner & Persistence
        </Text>
        <Flex gap="3" wrap="wrap">
          <SummaryStat label="Actions" value={summary.planner_actions} mutedLabel />
          <SummaryStat label="Planned Cards" value={summary.planned_cards} mutedLabel />
          <SummaryStat label="Filled Cards" value={summary.filled_cards} mutedLabel />
          <SummaryStat label="Cards Created" value={metrics.cards_created} mutedLabel />
          <SummaryStat label="Cards Reused" value={summary.reuse_card_ids.length} mutedLabel />
        </Flex>

        {details.length > 0 ? (
          <Table.Root size="1" variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Order</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Existing Card</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Planned Kind</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {details.map(detail => (
                <Table.Row key={`${detail.action}-${detail.order_index}`}>
                  <Table.Cell>{detail.action}</Table.Cell>
                  <Table.Cell>{detail.order_index}</Table.Cell>
                  <Table.Cell>{detail.existing_card_id ?? "–"}</Table.Cell>
                  <Table.Cell>{detail.planned_card_kind ?? "–"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          <Text size="2" color="gray">
            No planner action details recorded.
          </Text>
        )}

        {summary.reuse_card_ids.length > 0 && (
          <Box>
            <Text size="2" weight="bold" mb="1">
              Reused Card IDs
            </Text>
            <CodeBlock value={summary.reuse_card_ids.join("\n")} />
          </Box>
        )}

        {summary.created_card_ids.length > 0 && (
          <Box>
            <Text size="2" weight="bold" mb="1">
              Created Card IDs
            </Text>
            <CodeBlock value={summary.created_card_ids.join("\n")} />
          </Box>
        )}
      </Flex>
    </Card>
  );
};

const EventLogSection: FC<{
  events: SmartEngineRunEvent[];
  timing?: SmartEngineRunPayload["timing"];
}> = ({ events, timing }) => {
  if (events.length === 0) {
    return (
      <Card>
        <Text size="3" weight="bold">
          Event Log
        </Text>
        <Text size="2" color="gray">
          No events recorded.
        </Text>
      </Card>
    );
  }

  const runStart = timing?.started_at ? DateTime.fromISO(timing.started_at) : null;

  return (
    <SimpleAccordion title="Event Log" defaultOpen>
      <Card>
        <Table.Root size="1" variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Event</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Stage</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>+Δ</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Duration</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {events.map((event, index) => {
              let delta = "–";
              if (runStart && event.timestamp) {
                const seconds = DateTime.fromISO(event.timestamp).diff(runStart, "seconds").seconds;
                if (seconds != null) {
                  delta = `+${seconds.toFixed(2)}s`;
                }
              }

              return (
                <Table.Row key={`${event.event}-${index}-${event.timestamp}`}>
                  <Table.Cell>{event.event}</Table.Cell>
                  <Table.Cell>{event.stage ?? "–"}</Table.Cell>
                  <Table.Cell>{delta}</Table.Cell>
                  <Table.Cell>{formatDuration(event.duration_seconds)}</Table.Cell>
                  <Table.Cell>
                    {event.error_type ? (
                      <Text color="red">
                        {event.error_type ?? "Error"}: {event.error_message ?? ""}
                      </Text>
                    ) : (
                      "–"
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Card>
    </SimpleAccordion>
  );
};

const SummaryStat: FC<{
  label: string;
  value?: unknown;
  mutedLabel?: boolean;
  children?: React.ReactNode;
}> = ({ label, value, mutedLabel = false, children }) => {
  const display = children ?? (
    <Text
      size="2"
      style={{
        wordBreak: "break-word",
        letterSpacing: "0.3px",
        fontFamily: "var(--font-family-code, var(--font-family-base))",
      }}
    >
      {value == null || value === "" ? "–" : JSON.stringify(value)}
    </Text>
  );

  return (
    <Flex direction="column" gap="1" style={{ minWidth: 160 }}>
      <Text size="1" color={mutedLabel ? "gray" : "gray"}>
        {label}
      </Text>
      {display}
    </Flex>
  );
};

const InfoBadge: FC<{ label: string; value?: string | null; logfireQuery?: string }> = ({
  label,
  value,
  logfireQuery,
}) => {
  if (!value) {
    return null;
  }

  const display =
    typeof value === "string" || typeof value === "number" || typeof value === "boolean"
      ? String(value)
      : JSON.stringify(value, null, 2);

  const handleClick = () => {
    if (!logfireQuery) {
      return;
    }
    openLogfireSearch(logfireQuery);
  };

  return (
    <Flex
      align="center"
      gap="2"
      style={{
        background: "var(--gray-3)",
        borderRadius: 9999,
        padding: "6px 10px",
      }}
    >
      <Text size="1" color="gray">
        {label}
      </Text>
      <Flex align="center" gap="2">
        <Text
          size="1"
          style={{
            fontFamily: "var(--font-family-code)",
            cursor: logfireQuery ? "pointer" : "inherit",
            textDecoration: logfireQuery ? "underline" : "none",
          }}
          onClick={logfireQuery ? handleClick : undefined}
        >
          {display}
        </Text>
        <CopyValue value={display} />
      </Flex>
    </Flex>
  );
};

const CopyValue: FC<{ value: string }> = ({ value }) => {
  const handleCopy = () => {
    void navigator.clipboard.writeText(value);
  };
  return (
    <Button size="1" variant="soft" onClick={handleCopy} aria-label="Copy value">
      Copy
    </Button>
  );
};

const CodeBlock: FC<{ value: string }> = ({ value }) => (
  <pre
    style={{
      maxHeight: 360,
      overflow: "auto",
      background: "var(--gray-2)",
      padding: "12px",
      borderRadius: "8px",
      fontFamily: "var(--font-family-code)",
      fontSize: 12,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      border: "1px solid var(--gray-4)",
    }}
  >
    {value}
  </pre>
);

export default SmartEngineRunPage;
